'use client';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { REQUEST_ID_HEADER } from '#pplx/frontend/fetcher/constants.js';
import { getBackendErrorCode, getFetcherErrorCode } from '#pplx/frontend/fetcher/errors.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { getRumSessionId, startTimer } from '#pplx/frontend/metrics/browser.js';
import { CLIENT_SEARCH_PLACEHOLDER } from '#pplx/frontend/stream/createPlaceholderResults.js';
import { DEFAULT_SEARCH_PLACEHOLDER } from '#pplx/frontend/stream/createPlaceholderSearchResult.js';
import { mergeBlocksWithDiffBlocks } from '#pplx/frontend/stream/dataTransformation.js';
import { PplxStreamError } from '#pplx/frontend/stream/errors.js';
import { streamEvents } from '#pplx/frontend/stream/events.js';
import { filterNullishValuesMiddleware, processMessageWithMiddlewares, } from '#pplx/frontend/stream/middlewares.js';
import { addEntryToThreads, createRateLimitedFunction, isSubmitParams, } from '#pplx/frontend/stream/utils.js';
import { streamClient } from '#pplx/frontend/typed-clients/stream/index.js';
import { createZustandContext } from '#pplx/frontend/zustand-context/createZustandContext.js';
import { sseClient } from '#pplx_generated/typespec/rest/sse/client.js';
import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
const WEB_VERSION = '2.18';
const ASK_TERMINAL_ERROR_CODES = ['BASIC_TIER_RESTRICTED'];
const EMPTY_ARRAY = Object.freeze([]);
const messageStreamMeta = new Map();
/**
 * Get the active stream by ID
 * @param state - Current state of the stream
 * @param id - ID of the stream
 * @returns Active stream
 */
function getStream(state, id) {
    return id ? state.streams[id] : undefined;
}
/**
 * Get the active stream by entry ID
 * @param state - Current state of the stream
 * @param entryId - Entry ID to get the stream for
 * @returns Active stream
 */
function getStreamByEntryId(state, entryId) {
    const streams = Object.values(state.streams);
    const length = streams.length;
    // Most recent stream will be at the end of the array
    for (let i = length - 1; i >= 0; i--) {
        const stream = streams[i];
        if (stream.entryId === entryId && state.activeStreams.includes(stream.id.description)) {
            return stream;
        }
    }
    return undefined;
}
function streamMatchesReconnectOptions(stream, options) {
    if (options?.frontendContextUuid) {
        return (stream.params?.frontend_context_uuid === options.frontendContextUuid ||
            stream.placeholderChunk?.frontend_context_uuid === options.frontendContextUuid);
    }
    return !!(options?.contextUuid &&
        (stream.threadId === options.contextUuid ||
            stream.placeholderChunk?.context_uuid === options.contextUuid));
}
function hasWorkflowBlock(message) {
    return (message.blocks?.some((block) => block.intended_usage === 'workflow_root' ||
        !!block.workflow_block ||
        block.diff_block?.field === 'workflow_block') ?? false);
}
function getActiveUnassignedSubmitStreamForReconnect(state, options) {
    const activeStreamIds = new Set(state.activeStreams);
    const streams = Object.values(state.streams);
    const length = streams.length;
    for (let i = length - 1; i >= 0; i--) {
        const stream = streams[i];
        if (activeStreamIds.has(stream.id.description) &&
            !stream.isReconnect &&
            !stream.entryId &&
            !stream.abortController?.signal.aborted &&
            streamMatchesReconnectOptions(stream, options)) {
            return stream;
        }
    }
    return undefined;
}
function getActiveNonReconnectStreamByEntryId(state, entryId) {
    const activeStreamIds = new Set(state.activeStreams);
    return Object.values(state.streams).find((stream) => stream.entryId === entryId &&
        activeStreamIds.has(stream.id.description) &&
        !stream.isReconnect &&
        !stream.abortController?.signal.aborted);
}
/**
 * Get the active stream by thread ID
 * @param state - Current state of the stream
 * @param threadId - Thread ID to get the stream for
 * @returns Active stream
 */
function getStreamByThreadId(state, threadId) {
    const streams = Object.values(state.streams);
    const length = streams.length;
    // Most recent stream will be at the end of the array
    for (let i = length - 1; i >= 0; i--) {
        const stream = streams[i];
        if (stream.threadId === threadId && state.activeStreams.includes(stream.id.description)) {
            return stream;
        }
    }
    return undefined;
}
// We're storing a max of 50 streams at a time to curb resource usage, though
// browser default is generally 100. SSE connections are multiplexed over http/2,
// so we can afford to have a lot of streams open.
const MAX_ACTIVE_STREAMS = 50;
// We're storing a max of 100 threads at a time to curb memory usage.
const MAX_THREADS = 100;
// We're checking every 1 seconds to see if we need to reconnect any streams
const RECONNECT_INTERVAL = 1000;
// Persist the transient-thread registry to sessionStorage so it survives a
// page refresh within the same tab. sessionStorage matches the desired
// semantics: per-tab, cleared on tab close, never written by other tabs.
// In-memory state is the source of truth; storage is just a mirror so a
// fresh store init can rehydrate.
const TRANSIENT_THREADS_STORAGE_KEY = 'pplx:transient-threads';
function readTransientThreadsFromStorage() {
    if (typeof window === 'undefined')
        return {};
    try {
        const raw = window.sessionStorage.getItem(TRANSIENT_THREADS_STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {};
    }
}
function writeTransientThreadsToStorage(next) {
    if (typeof window === 'undefined')
        return;
    try {
        window.sessionStorage.setItem(TRANSIENT_THREADS_STORAGE_KEY, JSON.stringify(next));
    }
    catch {
        // sessionStorage quota or disabled — fall through; in-memory registry still works
    }
}
const NON_RECONNECTABLE_STREAM_FAILURE_CODES = new Set([
    'FETCHER_SSE_APPLICATION_ERROR',
    'FETCHER_CLOUDFLARE_403_ERROR',
    'FETCHER_SSE_CLOUDFLARE_403_ERROR',
    'FETCHER_SSE_TERMINAL_STATUS_ERROR',
]);
export function _isNonReconnectableStreamFailureCode(code) {
    return code != null && NON_RECONNECTABLE_STREAM_FAILURE_CODES.has(code);
}
export function _getReconnectCandidateEntryIds(state) {
    const candidates = new Set(Object.values(state.entries)
        .filter((entry) => entry.reconnectable === true)
        .map((entry) => entry.backend_uuid));
    for (const erroredStream of Object.values(state.erroredStreams)) {
        if (erroredStream.isReconnect &&
            erroredStream.entryId &&
            !state.entries[erroredStream.entryId]) {
            candidates.add(erroredStream.entryId);
        }
    }
    return candidates;
}
// Match the SSE fetcher's backoff cap and stop after a small number of
// automatic attempts. Progress clears the counter; manual retries still work.
const RECONNECT_BACKOFF_MAX_MS = 64_000;
const MAX_AUTOMATIC_RECONNECT_ATTEMPTS = 6;
const reconnectBackoffByEntryId = new Map();
/**
 * Returns true while an entry may not start an automatic reconnect, because it
 * has used up its attempts or is still inside its backoff window. Read-only.
 */
export function _isReconnectBackoffActive(entryId) {
    const backoff = reconnectBackoffByEntryId.get(entryId);
    if (!backoff)
        return false;
    return backoff.attempts >= MAX_AUTOMATIC_RECONNECT_ATTEMPTS || Date.now() < backoff.nextAttemptAt;
}
/**
 * Counts an automatic reconnect attempt that is actually starting and schedules
 * the next backoff window (exponential with jitter). Entries with no recorded
 * failure may retry immediately, but every attempt counts toward the limit.
 */
export function _recordReconnectAttempt(entryId, hasRecordedFailure) {
    const backoff = reconnectBackoffByEntryId.get(entryId) ?? { attempts: 0, nextAttemptAt: 0 };
    const delayMs = hasRecordedFailure
        ? Math.min(RECONNECT_INTERVAL * 2 ** backoff.attempts, RECONNECT_BACKOFF_MAX_MS)
        : 0;
    const jitter = Math.random() * 0.3 + 0.85; // +/- 15%
    reconnectBackoffByEntryId.set(entryId, {
        attempts: backoff.attempts + 1,
        nextAttemptAt: Date.now() + delayMs * jitter,
    });
}
export function _clearReconnectBackoff(entryId) {
    reconnectBackoffByEntryId.delete(entryId);
}
/**
 * Returns true when a stream closed cleanly only to hand off to an automatic
 * reconnect, so its entry should stay PENDING rather than settle to COMPLETED.
 * The backend marks this handoff `reconnectable: true`; the true end of a turn
 * is non-reconnectable. Local aborts and exhausted reconnect attempts still
 * settle, so an entry cannot stay in-flight indefinitely.
 */
export function _isAwaitingReconnect(entryId, entry, stream) {
    return (entry?.reconnectable === true &&
        !stream.abortController?.signal.aborted &&
        (reconnectBackoffByEntryId.get(entryId)?.attempts ?? 0) < MAX_AUTOMATIC_RECONNECT_ATTEMPTS);
}
/**
 * Ingest a message into the stream state
 * Test only.
 * @param prevState - Previous state of the stream
 * @param streamId - ID of the stream
 * @param message - Message to ingest
 * @returns New partial state of the stream store
 */
export function ingest(prevState, streamId, rawMessage) {
    const currentStream = getStream(prevState, streamId);
    // If the stream is not active, don't ingest any more messages
    if (!currentStream || !prevState.activeStreams.includes(streamId)) {
        return { newState: prevState };
    }
    let firstEntrySlug;
    if (currentStream.threadId) {
        const firstEntryId = prevState.threads.find((thread) => thread.id === currentStream.threadId)?.entryIds?.[0] ??
            undefined;
        firstEntrySlug = firstEntryId ? prevState.entries[firstEntryId]?.thread_url_slug : undefined;
    }
    const message = processMessageWithMiddlewares(prevState.middlewares, rawMessage, firstEntrySlug);
    if (!message) {
        return { newState: prevState };
    }
    const threadId = message.context_uuid;
    const entryId = message.backend_uuid;
    const extraEntryId = message.uuid;
    // Check if this stream is a reconnect (e.g. after a connection drop).
    const isAutoReconnect = !!(currentStream.entryId && currentStream.isReconnect);
    // If the stream is not yet associated with a thread or entry,
    // associate it with the current thread and entry.
    const isFirstMessage = !isAutoReconnect && (!currentStream.threadId || !currentStream.entryId);
    // If the message is a placeholder chunk, we need to update the stream
    // to have a placeholder chunk.
    if (entryId === DEFAULT_SEARCH_PLACEHOLDER || entryId === CLIENT_SEARCH_PLACEHOLDER) {
        return {
            newState: {
                streams: {
                    ...prevState.streams,
                    [streamId]: {
                        ...currentStream,
                        placeholderChunk: message,
                    },
                },
            },
        };
    }
    // If this is a placeholder for an existing entry (not using CLIENT_SEARCH_PLACEHOLDER),
    // we need to update that entry with placeholder content but preserve the UUID
    if ((message.status === 'PENDING' && message.blocks?.length === 0) ||
        message.status === 'RESUMING') {
        // Update the existing entry with placeholder content
        return {
            newState: {
                ...ingestMessage(prevState, message, {
                    isFirstMessage,
                    placeholderChunk: currentStream.placeholderChunk,
                    streamId: currentStream.id.description,
                }),
                streams: {
                    ...prevState.streams,
                    [streamId]: {
                        ...currentStream,
                        threadId: message.context_uuid,
                        entryId,
                        extraEntryId,
                        placeholderChunk: message,
                    },
                },
            },
            status: message.status,
        };
    }
    if (!threadId || !entryId) {
        return { newState: prevState };
    }
    if (currentStream.isReconnect) {
        const activeSubmitStream = getActiveNonReconnectStreamByEntryId(prevState, entryId);
        if (activeSubmitStream && activeSubmitStream.id.description !== streamId) {
            logger.info('[stream] skipping reconnect ingest for active submit stream', {
                stream_id: streamId,
                active_submit_stream_id: activeSubmitStream.id.description,
                entry_id: entryId,
                cursor: message.cursor,
            });
            currentStream.abortController?.abort('Skipped duplicate reconnect stream');
            return { newState: prevState };
        }
    }
    let newStream = currentStream;
    if (isFirstMessage || !currentStream.threadId || !currentStream.entryId) {
        newStream = {
            ...currentStream,
            threadId,
            entryId,
            extraEntryId,
        };
    }
    // So this is where we actually process and ingest the message into the state.
    // If processing fails, preserve the last valid content, record the stream
    // error, and settle ambiguous PENDING state to COMPLETED.
    try {
        return {
            newState: {
                ...ingestMessage(prevState, message, {
                    isFirstMessage,
                    placeholderChunk: currentStream.placeholderChunk,
                    streamId: currentStream.id.description,
                }),
                streams: {
                    ...prevState.streams,
                    [streamId]: newStream,
                },
            },
            status: message.status,
        };
    }
    catch (e) {
        // Abort the stream to prevent any further messages from being ingested
        // This is to prevent further messing up the state and upstream renderer.
        currentStream?.abortController?.abort('Failed to ingest message');
        return {
            newState: handleStreamError(prevState, streamId, new PplxStreamError('STREAM_FAILED_INGEST_ERROR', {
                cause: e,
                details: {
                    request_id: streamId,
                    backend_uuid: currentStream?.entryId ?? 'unknown',
                },
            })),
            status: 'FAILED',
        };
    }
}
/**
 * Ingest a message into the stream state
 * @param prevState - Previous state of the stream
 * @param message - Message to ingest
 * @param opts - Options for the ingestion
 * @returns New partial state of the stream store
 */
export function ingestMessage(prevState, message, { isFirstMessage, placeholderChunk, streamId, threadEntryPosition = 'append', }) {
    const threadId = message.context_uuid;
    const entryId = message.backend_uuid;
    // It's safe to continue to write to thread/entry repos here
    // since it's not changing the active stream pointer.
    let threads = Array.from(prevState.threads);
    const entries = { ...prevState.entries };
    let formAcknowledgements = prevState.formAcknowledgements;
    if (threads.length >= MAX_THREADS) {
        // Gotta evict the oldest thread
        const threadToEvict = threads[0];
        threads = threads.slice(1);
        // If the thread exists, delete all the entries in the thread
        if (threadToEvict) {
            if (formAcknowledgements?.[threadToEvict.id]) {
                const retained = { ...formAcknowledgements };
                delete retained[threadToEvict.id];
                formAcknowledgements = retained;
            }
            for (const entryId of threadToEvict.entryIds) {
                delete entries[entryId];
            }
        }
    }
    threads = addEntryToThreads(threads, threadId, entryId, { position: threadEntryPosition });
    const shouldMergeBlocks = message.status !== 'COMPLETED' || hasWorkflowBlock(message);
    // If a non-workflow message is final, overwrite the existing entry.
    // Since we assume the last message always contains the final answer,
    if (message.status === 'COMPLETED' && !shouldMergeBlocks) {
        // Preserve collection_info from the existing entry when the final message
        // doesn't include it (the backend may omit it and the filterNullishValues
        // middleware strips null/undefined fields, causing the space context to be lost).
        const existingEntry = entries[entryId];
        const collection_info = message.collection_info ?? existingEntry?.collection_info;
        const query_display_metadata = message.query_display_metadata ?? existingEntry?.query_display_metadata;
        const parent_info = message.parent_info ?? existingEntry?.parent_info;
        const query_source = message.query_source ?? existingEntry?.query_source;
        entries[entryId] = {
            ...message,
            ...(collection_info && { collection_info }),
            ...(query_display_metadata && { query_display_metadata }),
            ...(parent_info && { parent_info }),
            ...(query_source && { query_source }),
        };
    }
    // NOTE: typedef is wrong here, blocks indeed can be undefined
    // TODO: Fix typedef
    else if (message.blocks) {
        const existingEntry = entries[entryId];
        let entry = existingEntry;
        // Ok, we have an entry, make sure it's actually
        // the same entry and not an older entry and we're rewriting it.
        if (entry && entry.uuid !== message.uuid) {
            // This is a rewrite, use the placeholder chunk instead
            entry = placeholderChunk;
        }
        else if (!entry) {
            // If the entry doesn't exist, use the placeholder chunk
            entry = placeholderChunk;
        }
        const frontend_context_uuid = entry?.frontend_context_uuid ?? message.frontend_context_uuid ?? streamId;
        const frontend_uuid = entry?.frontend_uuid ?? message.frontend_uuid ?? streamId;
        // Don't override query_str
        const query_str = entry?.query_str ?? message.query_str;
        // Don't override thread_title
        const thread_title = entry?.thread_title ?? message.thread_title;
        // Preserve collection_info from the placeholder/existing entry when the streaming
        // message doesn't include it (space context would otherwise be lost).
        const collection_info = entry?.collection_info ?? message.collection_info;
        const query_display_metadata = message.query_display_metadata ?? entry?.query_display_metadata;
        const parent_info = message.parent_info ?? entry?.parent_info;
        const query_source = message.query_source ?? entry?.query_source ?? existingEntry?.query_source;
        // Prefer attachments from the streaming message when present (they contain signed URLs),
        // but fall back to existing entry attachments if the message has none.
        const attachments = message.attachments && message.attachments.length > 0
            ? message.attachments
            : (entry?.attachments ?? []);
        // When MCP auth happens `connector_auth_info` is sent to the frontent and the stream stops
        // We know auth has finished when the stream resumes and `connector_auth_info` is no longer in the message
        // So we need to clear it from the entry
        const shouldClearConnectorAuth = entry?.connector_auth_info != null && !('connector_auth_info' in message) && !isFirstMessage;
        // When insufficient_credits pauses the stream, `locked_reason` is set on the entry.
        // When credits are added and ASI resumes, new messages won't contain `locked_reason`.
        // Clear it so the blocking banner disappears.
        const shouldClearLockedReason = entry?.locked_reason != null && !('locked_reason' in message) && !isFirstMessage;
        // If the message is not final, merge the blocks into the existing entry.
        const newEntry = {
            ...(entry ?? {}),
            ...message,
            frontend_context_uuid,
            frontend_uuid,
            query_str,
            thread_title,
            collection_info,
            query_source,
            ...(parent_info && { parent_info }),
            attachments,
            ...(query_display_metadata && { query_display_metadata }),
            // Explicitly clear connector_auth_info when auth is completed
            ...(shouldClearConnectorAuth && { connector_auth_info: undefined }),
            // Explicitly clear locked_reason when task resumes after insufficient credits
            ...(shouldClearLockedReason && { locked_reason: undefined }),
            // If the message is the first message, this means we're resuming an entry
            // so we just wipe the existing blocks and start fresh.
            blocks: mergeBlocksWithDiffBlocks(isFirstMessage ? EMPTY_ARRAY : (entry?.blocks ?? EMPTY_ARRAY), message.blocks),
        };
        entries[entryId] = newEntry;
    }
    const meta = messageStreamMeta.get(entryId);
    messageStreamMeta.set(entryId, {
        start: meta?.start ?? Date.now(),
        latest: Date.now(),
        attempts: meta?.attempts ?? 1,
    });
    return { threads, entries, formAcknowledgements };
}
/**
 * Handle a stream error without inventing a backend FAILED state.
 * @param state - Current state of the stream
 * @param id - ID of the stream
 * @param error - Error that occurred
 * @returns New partial state of the stream store
 */
function handleStreamError(state, id, error) {
    const backendErrorCode = getBackendErrorCode(error);
    logger.error('Error in reading stream', error, {
        backendErrorCode,
    });
    const stream = getStream(state, id);
    if (!stream) {
        return state;
    }
    const entryId = stream?.entryId;
    if (!entryId) {
        return failUnboundStream(state, id, { ...stream, backendErrorCode });
    }
    const failureCode = getFetcherErrorCode(error);
    if (_isNonReconnectableStreamFailureCode(failureCode)) {
        _clearReconnectBackoff(entryId);
    }
    const { promise, abortController, ...erroredStream } = stream;
    const entry = state.entries[entryId];
    // Keep a turn that will reconnect PENDING so its stop button stays visible.
    const shouldSettle = entry?.status === 'PENDING' &&
        (_isNonReconnectableStreamFailureCode(failureCode) ||
            !_isAwaitingReconnect(entryId, entry, stream));
    return {
        activeStreams: state.activeStreams.filter((streamId) => streamId !== id),
        ...(backendErrorCode
            ? {
                streams: {
                    ...state.streams,
                    [id]: { ...stream, backendErrorCode },
                },
            }
            : {}),
        erroredStreams: {
            ...state.erroredStreams,
            [entryId]: {
                ...erroredStream,
                code: failureCode,
                backendErrorCode,
            },
        },
        ...(shouldSettle
            ? {
                entries: {
                    ...state.entries,
                    [entryId]: {
                        ...entry,
                        status: backendErrorCode ? 'FAILED' : 'COMPLETED',
                        ...(backendErrorCode ? { reconnectable: false } : {}),
                    },
                },
            }
            : {}),
    };
}
/**
 * A submit stream that dies before its first message never binds an entry, so
 * nothing else settles its optimistic placeholder: the thread keeps rendering
 * it as PENDING ("Thinking") and the follow-up input stays locked until reload.
 * Mark the placeholder FAILED and non-reconnectable (there is no entry to
 * resume) so the thread renders the failure and unlocks.
 */
function failUnboundStream(state, id, stream) {
    if (!stream.placeholderChunk || stream.abortController?.signal.aborted) {
        return state;
    }
    return {
        streams: {
            ...state.streams,
            [id]: {
                ...stream,
                placeholderChunk: {
                    ...stream.placeholderChunk,
                    status: 'FAILED',
                    reconnectable: false,
                },
            },
        },
    };
}
const INITIAL_MIDDLEWARES = Object.freeze([filterNullishValuesMiddleware]);
/**
 * Get the thread messages for a stream
 * @param state - Current state of the stream
 * @param stream - The stream to get thread messages for
 * @returns Array of messages in the thread
 */
function getThreadMessages(state, stream) {
    const thread = state.threads.find((thread) => thread.id === stream.threadId);
    return (thread?.entryIds.map((entryId) => state.entries[entryId]) ?? EMPTY_ARRAY).filter((entry) => entry != null);
}
/**
 * Handle a stream completed event
 * @param state - Current state of the stream
 * @param id - ID of the stream
 */
function handleStreamCompleted(state, id) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    const message = stream.entryId ? state.entries[stream.entryId] : undefined;
    messageStreamMeta.delete(stream.entryId ?? '');
    streamEvents.emit('completed', {
        stream,
        thread: getThreadMessages(state, stream),
        message,
    });
}
/**
 * Handle a stream progress event
 * @param state - Current state of the stream
 * @param id - ID of the stream
 * @param rawMessage - The raw message received
 * @param isFirstMessage - Whether this is the first message for the stream
 */
function handleStreamProgress(state, id, rawMessage, isFirstMessage) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    const message = state.entries[rawMessage.backend_uuid] ?? rawMessage;
    streamEvents.emit('progress', {
        stream,
        thread: getThreadMessages(state, stream),
        message,
        isFirstMessage,
    });
}
function handleStreamOpened(state, id) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    streamEvents.emit('opened', {
        stream,
        thread: getThreadMessages(state, stream),
        message: stream.entryId ? state.entries[stream.entryId] : undefined,
    });
}
/**
 * Handle a stream error event
 * @param state - Current state of the stream
 * @param id - ID of the stream
 * @param error - The error that occurred
 */
function handleStreamErrorEvent(state, id, error) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    const message = stream.entryId ? state.entries[stream.entryId] : undefined;
    streamEvents.emit('error', {
        stream,
        thread: getThreadMessages(state, stream),
        message,
        error,
        messageStreamMeta: messageStreamMeta.get(stream.entryId ?? ''),
    });
}
function handleStreamDisconnectedEvent(state, id, error) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    streamEvents.emit('disconnected', {
        stream,
        thread: getThreadMessages(state, stream),
        message: stream.entryId ? state.entries[stream.entryId] : undefined,
        error,
        messageStreamMeta: messageStreamMeta.get(stream.entryId ?? ''),
    });
}
/**
 * Handle a stream created event
 * @param state - Current state of the stream
 * @param id - ID of the stream
 * @param query - The query string
 * @param params - The submission parameters
 */
function handleStreamCreated(state, id, query, params, analyticsPolicy) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    streamEvents.emit('created', {
        analyticsPolicy,
        stream,
        thread: getThreadMessages(state, stream),
        message: stream.entryId ? state.entries[stream.entryId] : undefined,
        query,
        params,
    });
}
/**
 * Handle a stream aborted event
 * @param state - Current state of the stream
 * @param id - ID of the stream
 */
function handleStreamAborted(state, id) {
    const stream = getStream(state, id);
    if (!stream) {
        return;
    }
    streamEvents.emit('aborted', {
        stream,
        thread: getThreadMessages(state, stream),
        message: stream.entryId ? state.entries[stream.entryId] : undefined,
    });
}
/**
 * This is the internal store that is used to manage the stream state.
 * Only exported for testing purposes.
 */
export const _initializeStore = (set, get) => ({
    threads: [],
    entries: {},
    activeStreams: [],
    erroredStreams: {},
    transientThreads: readTransientThreadsFromStorage(),
    middlewares: INITIAL_MIDDLEWARES,
    streams: {},
    actions: {
        getEntryById: (entryId) => {
            return get().entries[entryId];
        },
        createStream: (params) => {
            return get().actions.createBackwardsCompatibleStream(params, {
                reason: 'create-stream',
                timer: startTimer(undefined, {
                    nowFromTimeOrigin: performance.now(),
                }),
            }).id;
        },
        retryStream: (entryId) => {
            const entry = get().entries[entryId];
            if (!entry) {
                return false;
            }
            const stream = get().erroredStreams[entryId];
            if (!stream) {
                return false;
            }
            const query = stream.placeholderChunk?.query_str;
            if (!query) {
                return false;
            }
            // Delete entryId from entries, threads, and erroredStreams so we're not stuck with half-errored entry
            set((state) => {
                const { entries, threads, erroredStreams } = state;
                const { [entryId]: _, ...newEntries } = entries;
                const { [entryId]: _errored, ...newErroredStreams } = erroredStreams;
                const newThreads = Array.from(threads);
                const threadIndex = threads.findIndex((thread) => thread.entryIds.includes(entryId));
                if (threadIndex === -1) {
                    return state;
                }
                newThreads.splice(threadIndex, 1, {
                    id: newThreads[threadIndex].id,
                    entryIds: newThreads[threadIndex].entryIds,
                });
                return {
                    threads: newThreads,
                    entries: newEntries,
                    erroredStreams: newErroredStreams,
                };
            });
            if (!isSubmitParams(stream.params)) {
                throw new Error('Stream params are not SubmitParams so cannot retry');
            }
            // Make a new rewrite stream
            return !!get().actions.createBackwardsCompatibleStream({
                query_str: query,
                params: {
                    ...stream.params,
                    // Triggers a rewrite
                    existing_entry_uuid: entryId,
                    query_source: 'retry',
                    frontend_context_uuid: entry.frontend_context_uuid,
                },
            }, {
                analyticsPolicy: stream.analyticsPolicy,
                placeholderChunk: stream.placeholderChunk,
                timer: startTimer(undefined, {
                    nowFromTimeOrigin: performance.now(),
                }),
                reason: 'retry-stream',
            });
        },
        reconnectStream: (entryId, options) => get().actions.reconnectStreamWithStatus(entryId, options).streamId,
        reconnectStreamWithStatus: (entryId, options) => {
            // If the stream is already active, return the existing stream ID
            const activeStream = getStreamByEntryId(get(), entryId);
            if (activeStream &&
                !!activeStream.promise &&
                !!activeStream.abortController &&
                !activeStream.abortController.signal.aborted) {
                return { streamId: activeStream.id, reusedStreamSettled: activeStream.promise };
            }
            // If we have an existing entry, use the cursor from the existing entry
            // This basically tells BE to stream from the same spot
            const existingEntry = get().entries[entryId];
            const pendingSubmitStream = existingEntry
                ? undefined
                : getActiveUnassignedSubmitStreamForReconnect(get(), options);
            if (pendingSubmitStream) {
                logger.info('[stream] skipping reconnect while submit stream is pending entry', {
                    entry_id: entryId,
                    pending_submit_stream_id: pendingSubmitStream.id.description,
                    requested_context_uuid: options?.contextUuid,
                    requested_frontend_context_uuid: options?.frontendContextUuid,
                });
                return {
                    streamId: pendingSubmitStream.id,
                    reusedStreamSettled: pendingSubmitStream.promise,
                };
            }
            const cursor = existingEntry?.cursor;
            // Otherwise, create a new stream
            const stream = get().actions.createBackwardsCompatibleStream({
                query_str: '',
                params: {
                    resume_entry_uuid: entryId,
                    cursor,
                },
            }, {
                isReconnect: true,
                timer: startTimer(undefined, {
                    nowFromTimeOrigin: performance.now(),
                }),
                reason: 'reconnect-stream',
            });
            const meta = messageStreamMeta.get(entryId);
            messageStreamMeta.set(entryId, {
                start: meta?.start ?? Date.now(),
                latest: meta?.latest ?? Date.now(),
                attempts: (meta?.attempts ?? 0) + 1,
            });
            return { streamId: stream.id, reusedStreamSettled: null };
        },
        createBackwardsCompatibleStream: (params, { analyticsPolicy, isReconnect, isNewThread, placeholderChunk, onFirstMessageReceived, timer, reason, }) => {
            /**
             * Get the active streams and make sure we don't exceed the max
             * number of active streams. If we do, abort the oldest stream.
             */
            const activeStreams = Array.from(get().activeStreams);
            const streams = { ...get().streams };
            if (activeStreams.length >= MAX_ACTIVE_STREAMS) {
                const streamId = activeStreams.shift();
                try {
                    if (streamId) {
                        streams[streamId]?.abortController?.abort('Forced abort');
                        streams[streamId] = {
                            ...streams[streamId],
                            promise: null,
                            abortController: null,
                        };
                    }
                }
                catch (e) {
                    logger.error('Failed to abort stream', new PplxStreamError('STREAM_FAILED_ABORT_ERROR', {
                        cause: e,
                        details: {
                            request_id: streamId ?? 'unknown',
                            backend_uuid: 'unknown',
                        },
                    }));
                }
            }
            /**
             * Create a new stream
             */
            const id = params.params?.frontend_uuid ?? safeRandomUUID();
            const abortController = new AbortController();
            const currentStream = {
                id: { description: id },
                promise: Promise.resolve(),
                abortController,
                isReconnect,
                isNewThread,
                entryId: params.params?.resume_entry_uuid,
                params: params.params,
                analyticsPolicy,
                timer,
            };
            timer.setGlobalContext({ request_id: id });
            let entryContextBound = false;
            const handlers = {
                open: () => {
                    handleStreamOpened(get(), id);
                },
                message: async (rawMessage) => {
                    // LEGACY SUPPORT: Only trigger the callback if the stream is not yet associated with a thread
                    // Otherwise the callback will be triggered every time a new message is received
                    const currentStream = getStream(get(), id);
                    // If the stream is aborted, don't ingest any more messages
                    // This is the case where we've aborted the stream so connection is severed,
                    // but there are still messages in the queue to be ingested.
                    if (currentStream?.abortController?.signal.aborted) {
                        return;
                    }
                    // Receiving a message proves the connection works; later disconnects
                    // should reconnect promptly rather than inherit stale backoff.
                    if (currentStream?.entryId) {
                        _clearReconnectBackoff(currentStream.entryId);
                    }
                    let isFirstMessage = false;
                    if (!currentStream?.threadId) {
                        isFirstMessage = true;
                        onFirstMessageReceived?.(rawMessage);
                    }
                    set((state) => {
                        const { newState, status } = ingest(state, id, rawMessage);
                        const erroredStreams = { ...state.erroredStreams };
                        delete erroredStreams[rawMessage.backend_uuid];
                        if (status === 'FAILED' && currentStream && currentStream.entryId) {
                            const { promise, abortController, ...erroredStream } = currentStream;
                            erroredStreams[currentStream.entryId] = erroredStream;
                        }
                        return { ...newState, erroredStreams };
                    }, undefined, 'ingest');
                    const entryId = getStream(get(), id)?.entryId;
                    if (entryId && !entryContextBound) {
                        entryContextBound = true;
                        timer.setGlobalContext({ entry_uuid: entryId });
                    }
                    // Emit the progress event
                    if (currentStream) {
                        handleStreamProgress(get(), id, rawMessage, isFirstMessage);
                    }
                },
            };
            const querySource = params.params?.query_source;
            const isOmnisearch = querySource === 'default_search';
            const headers = { [REQUEST_ID_HEADER]: id };
            logger.info('Starting stream', { request_id: id });
            currentStream.promise = (isReconnect
                ? streamClient.SSE('/rest/sse/perplexity_ask/reconnect/{resume_entry_uuid}', reason, {
                    path: {
                        resume_entry_uuid: params.params?.resume_entry_uuid ?? '',
                    },
                    params: {
                        cursor: params.params?.cursor,
                        reconnectInitialSnapshot: true,
                    },
                    signal: abortController.signal,
                    timer,
                    handlers,
                    headers,
                })
                : sseClient.SSE('/rest/sse/perplexity_ask', reason, {
                    terminalErrorCodes: ASK_TERMINAL_ERROR_CODES,
                    params: {
                        ...params,
                        params: {
                            ...params.params,
                            version: WEB_VERSION,
                            rum_session_id: getRumSessionId(),
                        },
                    },
                    signal: abortController.signal,
                    handlers,
                    metrics: {
                        omnisearch: isOmnisearch,
                        query_source: querySource,
                    },
                    timer,
                    headers,
                }))
                .then(() => {
                // A stream ending without an authoritative terminal message is
                // ambiguous, including after a local abort. Resolve a lingering
                // PENDING entry to COMPLETED so it cannot remain in-flight, unless
                // it is only waiting on an automatic reconnect.
                const state = get();
                const stream = getStream(state, id);
                if (stream?.entryId) {
                    const entry = state.entries[stream.entryId];
                    if (entry?.status === 'PENDING' &&
                        !_isAwaitingReconnect(stream.entryId, entry, stream)) {
                        const entryId = stream.entryId;
                        set((prev) => ({
                            entries: {
                                ...prev.entries,
                                [entryId]: {
                                    ...prev.entries[entryId],
                                    status: 'COMPLETED',
                                },
                            },
                        }), undefined, 'handleStreamCompletedPendingEntry');
                    }
                }
                handleStreamCompleted(get(), id);
            }, (e) => {
                const state = get();
                const stream = getStream(state, id);
                if (!stream) {
                    return;
                }
                set((state) => handleStreamError(state, id, new PplxStreamError('STREAM_FAILED_STREAM_ERROR', {
                    cause: e,
                    details: {
                        request_id: id,
                        backend_uuid: stream.entryId ?? 'unknown',
                    },
                })), undefined, 'handleStreamError');
                const entry = stream.entryId ? state.entries[stream.entryId] : undefined;
                const failureCode = getFetcherErrorCode(e);
                const isRetryableInterruption = !_isNonReconnectableStreamFailureCode(failureCode) &&
                    (stream.isReconnect || entry?.reconnectable === true);
                if (isRetryableInterruption) {
                    handleStreamDisconnectedEvent(state, id, e);
                }
                else {
                    handleStreamErrorEvent(get(), id, e);
                }
            })
                .finally(() => {
                // Whether the stream was aborted or completed, we need to remove it from the active streams.
                // We intentionally preserve `promise` and `abortController` on the stream object so that
                // maybeReconnect can check `abortController.signal.aborted` to skip explicitly-aborted streams.
                set(({ activeStreams }) => ({
                    activeStreams: activeStreams.filter((streamId) => streamId !== id),
                }), undefined, 'cleanupBackwardsCompatibleStream');
            });
            activeStreams.push(id);
            set({
                activeStreams,
                streams: { ...streams, [id]: currentStream },
            }, undefined, 'createBackwardsCompatibleStream');
            // Emit the created event
            handleStreamCreated(get(), id, params.query_str, params.params, analyticsPolicy);
            // Emit the aborted event
            currentStream.abortController?.signal.addEventListener('abort', () => {
                handleStreamAborted(get(), id);
            });
            // If the stream is a placeholder chunk, ingest it
            // This is after the stream is pushed to the active streams
            // because ingest() assumes the stream is already in the active streams
            if (placeholderChunk) {
                set((state) => ingest(state, id, placeholderChunk).newState, undefined, 'ingestPlaceholderChunk');
            }
            return currentStream;
        },
        setMiddlewares: (middlewares) => {
            set(() => ({
                middlewares: [...INITIAL_MIDDLEWARES, ...middlewares],
            }));
        },
        registerTransientThread: (uuid, meta) => {
            set((state) => {
                const next = {
                    ...state.transientThreads,
                    [uuid]: { ...meta, createdAt: Date.now() },
                };
                writeTransientThreadsToStorage(next);
                return { transientThreads: next };
            }, undefined, 'registerTransientThread');
        },
        unregisterTransientThread: (uuid) => {
            set((state) => {
                if (!(uuid in state.transientThreads))
                    return state;
                const { [uuid]: _removed, ...rest } = state.transientThreads;
                writeTransientThreadsToStorage(rest);
                return { transientThreads: rest };
            }, undefined, 'unregisterTransientThread');
        },
    },
});
export const streamContext = createZustandContext('StreamStateContext', () => create()(subscribeWithSelector(_initializeStore)));
const useSelector = streamContext.useSelector;
const useStore = streamContext.useStore;
export function useStreamStateMonitor() {
    const store = useStore();
    // Count an attempt only once the rate limiter admits it: a rejected call
    // opens no stream, so it must not use up the entry's attempt budget.
    const rateLimitedReconnect = useMemo(() => createRateLimitedFunction((entryId, hasRecordedFailure) => {
        _recordReconnectAttempt(entryId, hasRecordedFailure);
        return store.getState().actions.reconnectStream(entryId);
    }, { perMinute: 20, perHour: 120 }), [store]);
    useEffect(() => {
        function maybeReconnect() {
            const state = store.getState();
            const activeStreamIds = new Set(state.activeStreams);
            const activeEntryIds = new Set(Object.values(state.streams)
                .filter((stream) => activeStreamIds.has(stream.id.description))
                .map((stream) => stream.entryId));
            const candidateEntryIds = _getReconnectCandidateEntryIds(state);
            candidateEntryIds.forEach((entryId) => {
                const entry = state.entries[entryId];
                const stream = entry?.frontend_uuid ? state.streams[entry.frontend_uuid] : undefined;
                const erroredStream = state.erroredStreams[entryId];
                // Don't reconnect if the stream is explicitly aborted.
                if (stream && stream.abortController?.signal.aborted) {
                    return;
                }
                if (!_isNonReconnectableStreamFailureCode(erroredStream?.code) &&
                    activeStreamIds.size < MAX_ACTIVE_STREAMS &&
                    !activeEntryIds.has(entryId) &&
                    !_isReconnectBackoffActive(entryId)) {
                    // As long as we don't drop the entries from stream store memory, we will come
                    // back to reconnect streams we may have aborted due to max active streams
                    void rateLimitedReconnect(entryId, erroredStream != null);
                }
            });
        }
        maybeReconnect();
        let interval = setInterval(maybeReconnect, RECONNECT_INTERVAL);
        function handleOnline() {
            clearInterval(interval);
            maybeReconnect();
            interval = setInterval(maybeReconnect, RECONNECT_INTERVAL);
        }
        function handleOffline() {
            clearInterval(interval);
        }
        function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                handleOnline();
            }
        }
        window.addEventListener('online', handleOnline, true);
        window.addEventListener('offline', handleOffline, true);
        window.addEventListener('pageshow', handleOnline, true);
        document.addEventListener('visibilitychange', handleVisibilityChange, true);
        return () => {
            clearInterval(interval);
            window.removeEventListener('online', handleOnline, true);
            window.removeEventListener('offline', handleOffline, true);
            window.removeEventListener('pageshow', handleOnline, true);
            document.removeEventListener('visibilitychange', handleVisibilityChange, true);
        };
    }, [rateLimitedReconnect, store]);
}
export const useCreateBackwardsCompatibleStream = () => useSelector((state) => state.actions.createBackwardsCompatibleStream);
export const useReconnectStream = () => useSelector((state) => state.actions.reconnectStream);
export const useHasActiveStream = (streamId) => {
    const activeStreams = useSelector((state) => state.activeStreams);
    const streams = useSelector((state) => state.streams);
    const stream = Object.values(streams).find((stream) => stream.id.description === streamId);
    return !!(stream && activeStreams.includes(stream?.id.description));
};
export const useIsTransientThread = (uuid) => {
    const transientThreads = useSelector((state) => state.transientThreads);
    return !!uuid && uuid in transientThreads;
};
export const useTransientThreadMeta = (uuid) => {
    const transientThreads = useSelector((state) => state.transientThreads);
    return uuid ? transientThreads[uuid] : undefined;
};
export const useRegisterTransientThread = () => useSelector((state) => state.actions.registerTransientThread);
export const useUnregisterTransientThread = () => useSelector((state) => state.actions.unregisterTransientThread);
export function isStreamFailureReconnectable(args) {
    return !!args.reconnectable && !_isNonReconnectableStreamFailureCode(args.failureCode);
}
export function isStreamRecoveryPending(args) {
    if (!args.entryId || !args.hasStreamError || !isStreamFailureReconnectable(args)) {
        return false;
    }
    return ((reconnectBackoffByEntryId.get(args.entryId)?.attempts ?? 0) < MAX_AUTOMATIC_RECONNECT_ATTEMPTS);
}
export function shouldShowStreamErrorIndicator(args) {
    if (!args.hasStreamError || args.isReconnecting)
        return false;
    return args.isLatestEntry || !args.isReconnectFailure;
}
/**
 * Returns whether an errored stream still has automatic recovery attempts.
 * While recovery remains possible, suppress the terminal error banner. The
 * backend may close 200 streams due to polling timeouts, which is normal.
 */
export function useIsStreamReconnecting(args) {
    return isStreamRecoveryPending(args);
}
export const useStreamStore = () => useStore();
/**
 * Mutate an entry in the stream state
 * @returns A function that mutates an entry
 */
export const useMutateEntry = () => {
    const store = useStreamStore();
    return useCallback((entryId, updater) => {
        if (!entryId) {
            return;
        }
        store.setState((state) => {
            const entry = state.entries[entryId];
            const activeStream = getStreamByEntryId(state, entryId);
            // If we have an active stream, we have to cancel it
            // before we can mutate the entries
            if (activeStream &&
                !!activeStream.promise &&
                !!activeStream.abortController &&
                !activeStream.abortController.signal.aborted) {
                logger.error('Attempting to mutate entries while stream is active');
            }
            if (!entry || !updater) {
                return state;
            }
            const newEntry = updater(entry);
            if (!newEntry) {
                return state;
            }
            return {
                ...state,
                entries: { ...state.entries, [entryId]: newEntry },
            };
        }, undefined, 'mutateEntry');
    }, [store]);
};
/**
 * Mutate the entries in the stream state
 * @returns A function that mutates the entries
 */
export const useMutateEntries = () => {
    const store = useStreamStore();
    return useCallback((threadId, updater, { terminateStream = true } = {}) => {
        if (!threadId) {
            return;
        }
        store.setState((state) => {
            if (!threadId) {
                return state;
            }
            const activeStream = getStreamByThreadId(state, threadId);
            // If we have an active stream, we have to cancel it
            // before we can mutate the entries
            if (activeStream && terminateStream) {
                logger.error('Attempting to mutate entries while stream is active');
            }
            const threadIndex = state.threads.findIndex((thread) => thread.id === threadId);
            if (threadIndex === -1) {
                return state;
            }
            const thread = state.threads[threadIndex];
            if (!thread) {
                return state;
            }
            const entryIds = thread.entryIds;
            const entries = entryIds
                .map((entryId) => state.entries[entryId])
                .filter((entry) => entry != null);
            const newEntries = updater?.(entries);
            if (!newEntries) {
                return state;
            }
            const newThreads = Array.from(state.threads);
            newThreads.splice(threadIndex, 1, {
                ...thread,
                entryIds: newEntries.map((entry) => entry.backend_uuid),
            });
            return {
                ...state,
                entries: {
                    ...state.entries,
                    ...newEntries.reduce((acc, entry) => {
                        acc[entry.backend_uuid] = entry;
                        return acc;
                    }, {}),
                },
                threads: newThreads,
            };
        }, undefined, 'mutateEntries');
    }, [store]);
};
/**
 * Delete an entry in the stream state
 * @returns A function that deletes an entry
 */
export const useDeleteEntry = () => {
    const store = useStreamStore();
    return useCallback((entryId) => {
        if (!entryId) {
            return;
        }
        _clearReconnectBackoff(entryId);
        store.setState((state) => {
            const entry = state.entries[entryId];
            const activeStream = getStreamByEntryId(state, entryId);
            // If we have an active stream, we have to cancel it
            // before we can mutate the entries
            if (activeStream &&
                !!activeStream.promise &&
                !!activeStream.abortController &&
                !activeStream.abortController.signal.aborted) {
                activeStream.abortController?.abort('Forced abort');
            }
            if (!entry) {
                return state;
            }
            const newEntries = {
                ...state.entries,
            };
            delete newEntries[entryId];
            const newThreads = state.threads
                .map((thread) => {
                const entryIdIndex = thread.entryIds.indexOf(entryId);
                if (entryIdIndex === -1)
                    return thread;
                const newEntryIds = [...thread.entryIds];
                newEntryIds.splice(entryIdIndex, 1);
                if (!newEntryIds.length)
                    return undefined;
                return { ...thread, entryIds: newEntryIds };
            })
                .filter((thread) => !!thread);
            return {
                ...state,
                entries: newEntries,
                threads: newThreads,
            };
        }, undefined, 'deleteEntry');
    }, [store]);
};
/**
 * Delete entries in the stream state
 * @returns A function that deletes entries
 */
/**
 * Get the active stream by entry ID
 * @param entryId - Entry ID to get the stream for
 * @returns Active stream
 */
/**
 * Get the failed stream by entry ID
 * @param entryId - Entry ID to get the stream for
 * @returns Failed stream
 */
export function useGetErroredStreamByEntryId(entryId) {
    const store = useStreamStore();
    const streams = Object.values(store.getState().erroredStreams);
    const length = streams.length;
    // Most recent stream will be at the end of the array
    for (let i = length - 1; i >= 0; i--) {
        const stream = streams[i];
        if (stream.entryId === entryId) {
            return stream;
        }
    }
    return undefined;
}
export function useGetStreamTimer() {
    const store = useStreamStore();
    return useCallback((id) => store.getState().streams[id]?.timer, [store]);
}
/**
 * Check if there are any active streams
 * @returns True if there are any active streams, false otherwise
 */
export function useHasSomeActiveStreams() {
    const store = useStreamStore();
    return useCallback(() => {
        return store.getState().activeStreams.length > 0;
    }, [store]);
}
//# sourceMappingURL=stream.js.map